// In dev this stays as '/api' so Vite's proxy forwards to the local
// backend. In production we set VITE_API_BASE_URL to the absolute API
// origin (e.g. https://api.getpowermove.com) at build time.
const API_BASE = `${import.meta.env.VITE_API_BASE_URL ?? ''}/api`;

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

async function fetchApi<T>(
  endpoint: string,
  options?: RequestInit
): Promise<T> {
  const response = await fetch(`${API_BASE}${endpoint}`, {
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
    ...options,
  });

  const json: ApiResponse<T> = await response.json();

  if (!json.success) {
    throw new Error(json.error || 'API request failed');
  }

  return json.data as T;
}

// Users
export interface User {
  id: string;
  slack_id: string;
  email: string;
  name: string;
  level: string;
  scope_type: 'customer' | 'product' | null;
  manager_id: string | null;
  created_at: string;
  activity?: {
    total_updates: number;
    updates_last_7_days: number;
    last_update: string | null;
  };
}

export async function getUsers(): Promise<User[]> {
  return fetchApi<User[]>('/users');
}

export async function getUser(id: string): Promise<User> {
  return fetchApi<User>(`/users/${id}`);
}

export async function updateUserLevel(id: string, level: string): Promise<User> {
  return fetchApi<User>(`/users/${id}/level`, {
    method: 'PATCH',
    body: JSON.stringify({ level }),
  });
}

// Hashtags
export interface Hashtag {
  id: string;
  name: string;
  type: string;
  display_name: string | null;
  usage_count: number;
  last_used: string | null;
}

export async function getHashtags(): Promise<Hashtag[]> {
  return fetchApi<Hashtag[]>('/hashtags');
}

export async function updateHashtag(
  id: string,
  type: string,
  displayName?: string
): Promise<Hashtag> {
  return fetchApi<Hashtag>(`/hashtags/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({ type, display_name: displayName }),
  });
}

// Updates
export interface Update {
  id: string;
  author_id: string;
  raw_text: string;
  sentiment: string | null;
  created_at: string;
}

export async function getUpdates(days: number = 7): Promise<Update[]> {
  return fetchApi<Update[]>(`/updates?days=${days}`);
}

// Scheduled Reports
export type ScheduledReportType = 'product' | 'customer';

export interface ScheduledReportResponse {
  report_type: ScheduledReportType;
  week: number;
  year: number;
  content: object;
  formatted: string;
}

export async function generateScheduledReport(
  userId: string,
  reportType: ScheduledReportType
): Promise<ScheduledReportResponse> {
  return fetchApi<ScheduledReportResponse>('/scheduled-reports/generate', {
    method: 'POST',
    body: JSON.stringify({ user_id: userId, report_type: reportType }),
  });
}

// Admin
export interface SchedulerStatus {
  running: boolean;
  schedule: string;
  timezone: string;
  nextRun: string | null;
}

export async function getSchedulerStatus(): Promise<SchedulerStatus> {
  return fetchApi<SchedulerStatus>('/admin/scheduler');
}

export async function triggerRollups(): Promise<{ message: string }> {
  return fetchApi<{ message: string }>('/admin/rollups/trigger', {
    method: 'POST',
  });
}
